import { useEffect, useState } from "react";
import { useLang } from "@/i18n";
import { updateConsent, pushEvent } from "@/lib/analytics";
import { Cookie, X } from "lucide-react";

const KEY = "madeira_consent_v1";

export default function CookieBanner() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);

  useEffect(() => {
    const saved = localStorage.getItem(KEY);
    if (!saved) {
      setOpen(true);
    } else {
      try {
        const prefs = JSON.parse(saved);
        // Re-apply previously stored consent to GTM
        updateConsent(prefs);
      } catch {
        setOpen(true);
      }
    }
  }, []);

  const persist = (prefs) => {
    localStorage.setItem(
      KEY,
      JSON.stringify({ ...prefs, ts: new Date().toISOString() })
    );
    updateConsent(prefs);
    setOpen(false);
  };

  const acceptAll = () => {
    pushEvent("cookie_accept_all");
    persist({ analytics: true, marketing: true });
  };
  const rejectAll = () => {
    pushEvent("cookie_reject_all");
    persist({ analytics: false, marketing: false });
  };
  const saveSelection = () => {
    pushEvent("cookie_save_selection", { analytics, marketing });
    persist({ analytics, marketing });
  };

  if (!open) return null;

  const c = t.cookies;

  return (
    <div data-testid="cookie-banner" className="fixed bottom-0 inset-x-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6 animate-fade-up">
      <div className="max-w-5xl mx-auto bg-navy border border-gold/40 rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-5 sm:p-6 flex flex-col lg:flex-row lg:items-start gap-5">
          <div className="flex items-start gap-3 flex-1">
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gold/15 border border-gold/30 flex items-center justify-center">
              <Cookie className="w-5 h-5 text-gold" />
            </div>
            <div className="min-w-0">
              <h3 className="font-display text-lg sm:text-xl text-cream font-bold mb-1">{c.title}</h3>
              <p className="text-cream/70 text-sm leading-relaxed">{c.desc}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 lg:flex-shrink-0">
            <button
              data-testid="cookie-reject-btn"
              onClick={rejectAll}
              className="px-4 py-2.5 rounded-full text-sm font-semibold bg-navy-mid border border-navy-line text-cream hover:border-gold/50 transition"
            >
              {c.reject}
            </button>
            <button
              data-testid="cookie-settings-btn"
              onClick={() => setSettings((v) => !v)}
              className="px-4 py-2.5 rounded-full text-sm font-semibold bg-navy-mid border border-navy-line text-cream hover:border-gold/50 transition"
            >
              {c.settings}
            </button>
            <button
              data-testid="cookie-accept-btn"
              onClick={acceptAll}
              className="btn-primary px-5 py-2.5 rounded-full text-sm"
            >
              {c.accept}
            </button>
          </div>
        </div>

        {settings && (
          <div data-testid="cookie-settings-panel" className="border-t border-navy-line bg-navy-deep/40 p-5 sm:p-6 space-y-3">
            <Row title={c.essentialTitle} desc={c.essentialDesc} locked checked />
            <Row
              testid="cookie-toggle-analytics"
              title={c.analyticsTitle}
              desc={c.analyticsDesc}
              checked={analytics}
              onChange={() => setAnalytics((v) => !v)}
            />
            <Row
              testid="cookie-toggle-marketing"
              title={c.marketingTitle}
              desc={c.marketingDesc}
              checked={marketing}
              onChange={() => setMarketing((v) => !v)}
            />
            <div className="flex justify-end pt-2">
              <button
                data-testid="cookie-save-btn"
                onClick={saveSelection}
                className="btn-primary px-5 py-2.5 rounded-full text-sm"
              >
                {c.save}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ title, desc, checked, onChange, locked, testid }) {
  return (
    <label className={`flex items-start gap-3 ${locked ? "opacity-70" : "cursor-pointer"}`}>
      <span className="relative inline-block flex-shrink-0 mt-1">
        <input
          data-testid={testid}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={locked}
          className="sr-only peer"
        />
        <span className="block w-10 h-5 rounded-full bg-navy-mid border border-navy-line peer-checked:bg-gold peer-checked:border-gold transition" />
        <span className={`absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-cream transition-transform ${checked ? 'translate-x-5' : ''}`} />
      </span>
      <span className="min-w-0">
        <span className="block font-semibold text-cream text-sm">{title}</span>
        <span className="block text-cream/60 text-xs leading-relaxed">{desc}</span>
      </span>
    </label>
  );
}
